import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from '../services/axios';
import toast from 'react-hot-toast';
import { BellIcon, CheckIcon } from '@heroicons/react/24/outline';
import PageHeader from '../components/common/PageHeader';
import EmptyState from '../components/common/EmptyState';

export default function Notifications() {
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const res = await axios.get('/student/notifications', {
                params: {
                    unreadOnly: filter === 'unread',
                    limit: 50
                }
            });
            setItems(res.data.data || []);
            setUnread(res.data.unread ?? 0);
        } catch (err) {
            toast.error('Unable to load notifications');
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => {
        load();
    }, [load]);

    const markRead = async (id) => {
        try {
            await axios.put(`/student/notifications/${id}/read`);
            load();
        } catch (err) {
            toast.error('Could not mark as read');
        }
    };

    const markAll = async () => {
        try {
            await axios.put('/student/notifications/read-all');
            toast.success('All marked as read');
            load();
        } catch (err) {
            toast.error('Could not mark all as read');
        }
    };

    return (
        <div className="w-full max-w-3xl space-y-6">
            <PageHeader
                title="Notifications"
                description={`${unread} unread · only your own notifications are shown`}
                actions={
                    <div className="flex items-center gap-3">
                        <select
                            className="input-field text-sm !py-2 !mt-0 h-10"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            aria-label="Filter notifications"
                        >
                            <option value="all">All</option>
                            <option value="unread">Unread</option>
                        </select>
                        <button
                            type="button"
                            onClick={markAll}
                            className="inline-flex items-center justify-center gap-2 h-10 px-4 text-sm font-semibold text-brand-700 bg-brand-50 hover:bg-brand-100 border border-brand-200 rounded-xl transition-all shadow-sm whitespace-nowrap"
                        >
                            <CheckIcon className="w-4 h-4 text-brand-700 stroke-[2.5]" aria-hidden="true" />
                            <span>Mark all as read</span>
                        </button>
                    </div>
                }
            />

            {loading ? (
                <div className="space-y-3" role="status">
                    <div className="skeleton h-20" />
                    <div className="skeleton h-20" />
                    <span className="sr-only">Loading…</span>
                </div>
            ) : items.length === 0 ? (
                <EmptyState
                    icon={BellIcon}
                    title="No notifications"
                    description="You're all caught up. New updates will appear here."
                />
            ) : (
                <ul className="space-y-2">
                    {items.map((n) => (
                        <li
                            key={n.id}
                            className={`card animate-fade-up ${
                                !n.is_read ? '!border-l-4 !border-l-brand-600' : ''
                            }`}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <p className="font-medium text-ink-950">{n.title}</p>
                                    <p className="text-sm text-ink-600 mt-1">{n.body}</p>
                                    <p className="text-xs text-ink-400 mt-2">
                                        {n.category} ·{' '}
                                        {n.created_at
                                            ? new Date(n.created_at).toLocaleString()
                                            : ''}
                                    </p>
                                    {n.link_url && (
                                        <Link
                                            to={n.link_url}
                                            className="inline-block mt-2 text-sm font-semibold text-brand-700 hover:text-brand-800"
                                        >
                                            Open related page
                                        </Link>
                                    )}
                                </div>
                                {!n.is_read && (
                                    <button
                                        type="button"
                                        className="text-xs text-ink-600 hover:text-ink-950 underline shrink-0"
                                        onClick={() => markRead(n.id)}
                                    >
                                        Mark read
                                    </button>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
